/**
 * Lightweight analytics event tracker for DJILU.COM
 *
 * Usage:
 *   import { trackEvent } from "@/lib/analytics";
 *   trackEvent("channel_view_started", { channel_id: "abc" });
 *
 * Privacy: No passwords, tokens, card details, or message content tracked.
 * Rate: max 60 events/min client-side.
 */

const API = process.env.REACT_APP_BACKEND_URL || "";
let _eventCount = 0;
let _lastReset = Date.now();
const CLIENT_RATE_LIMIT = 60;
const RATE_WINDOW = 60000;

function getDeviceType() {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function getBrowser() {
  const ua = navigator.userAgent;
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Edg")) return "Edge";
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Safari")) return "Safari";
  if (ua.includes("Opera") || ua.includes("OPR")) return "Opera";
  return "Other";
}

function getOS() {
  const ua = navigator.userAgent;
  if (ua.includes("Win")) return "Windows";
  if (ua.includes("Mac")) return "macOS";
  if (ua.includes("Linux")) return "Linux";
  if (ua.includes("Android")) return "Android";
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  return "Other";
}

function getUserId() {
  try {
    const u = JSON.parse(localStorage.getItem("user_data") || "{}");
    return u.id || null;
  } catch {
    return null;
  }
}

export function trackEvent(eventType, extra = {}) {
  // Client-side rate limiting
  if (Date.now() - _lastReset > RATE_WINDOW) {
    _eventCount = 0;
    _lastReset = Date.now();
  }
  if (_eventCount >= CLIENT_RATE_LIMIT) return;
  _eventCount++;

  const payload = {
    event_type: eventType,
    user_id: getUserId(),
    device_type: getDeviceType(),
    browser: getBrowser(),
    operating_system: getOS(),
    language: document.documentElement.lang || navigator.language?.slice(0, 2) || "en",
    referrer: document.referrer?.slice(0, 500) || "",
    page_path: window.location.pathname,
    ...extra,
  };

  // Fire-and-forget via sendBeacon (best for page unloads) or fetch
  const url = `${API}/api/analytics/event`;
  const body = JSON.stringify(payload);

  if (navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
  } else {
    fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
  }
}

/**
 * Track channel view start. Returns a stop function for tracking duration.
 * Usage:
 *   const stop = trackChannelView("channel-id-123");
 *   // ... when user leaves or playback ends:
 *   stop();
 */
export function trackChannelView(channelId, categoryId, countryId) {
  if (!channelId) return () => {};
  const startTime = Date.now();
  let stopped = false;

  trackEvent("channel_view_started", {
    channel_id: channelId,
    category_id: categoryId || null,
    country_id: countryId || null,
  });

  return function stopViewing() {
    if (stopped) return;
    stopped = true;
    const duration = Math.round((Date.now() - startTime) / 1000);
    // Only record if viewed for at least 5 seconds (avoid inflation from rapid navigation)
    if (duration >= 5) {
      trackEvent("channel_view_ended", {
        channel_id: channelId,
        watch_duration_seconds: duration,
      });
    }
  };
}

/**
 * Track playback error
 */
export function trackPlaybackError(channelId, errorCode, errorContext) {
  trackEvent("playback_error", {
    channel_id: channelId || null,
    error_code: (errorCode || "").toString().slice(0, 100),
    error_context: (errorContext || "").slice(0, 500),
  });
}
